import { Box, BoxProps, styled } from '@mui/material'
import clsx from 'clsx'
import { ElementType, FC, ReactNode } from 'react'

// 1. Interfaz base reutilizable para todos los componentes de texto
export interface CustomTypographyProps extends Omit<BoxProps, 'textTransform'> {
  children?: ReactNode
  ellipsis?: boolean
  textTransform?: 'none' | 'capitalize' | 'uppercase' | 'lowercase'
}

// 2. StyledBox centralizado usando flexibilidad de props sin ensuciar el DOM
const StyledBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'ellipsis' && prop !== 'textTransform',
})<CustomTypographyProps>(({ ellipsis, textTransform }) => ({
  ...(ellipsis && {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }),
  ...(textTransform && {
    textTransform,
  }),
}))

// 3. Creador auxiliar (Factory) para evitar duplicación masiva de código
interface CreateTypographyOptions {
  component: ElementType
  defaultFontSize: string
  defaultFontWeight?: number | string
  defaultLineHeight?: string | number
}

const createTypographyComponent = ({
  component,
  defaultFontSize,
  defaultFontWeight = 500,
  defaultLineHeight = 1.5,
}: CreateTypographyOptions): FC<CustomTypographyProps> => {
  const TypographyComponent: FC<CustomTypographyProps> = ({
    children,
    className,
    ellipsis = false,
    textTransform,
    sx,
    ...props
  }) => (
    <StyledBox
      component={component}
      ellipsis={ellipsis}
      textTransform={textTransform}
      className={clsx(className)}
      sx={{
        margin: 0,
        fontSize: defaultFontSize,
        fontWeight: defaultFontWeight,
        lineHeight: defaultLineHeight,
        ...sx,
      }}
      {...props}
    >
      {children}
    </StyledBox>
  )

  TypographyComponent.displayName = `Typography(${typeof component === 'string' ? component : 'Custom'})`
  return TypographyComponent
}

// ============================================================================
// COMPONENTES ENCABEZADOS (Headings Semánticos)
// ============================================================================

export const H1 = createTypographyComponent({ component: 'h1', defaultFontSize: '1.75rem' }) // 28px
export const H2 = createTypographyComponent({ component: 'h2', defaultFontSize: '1.5rem' }) // 24px
export const H3 = createTypographyComponent({ component: 'h3', defaultFontSize: '1.125rem' }) // 18px
export const H4 = createTypographyComponent({
  component: 'h4',
  defaultFontSize: '1rem',
  defaultFontWeight: 600,
}) // 16px
export const H5 = createTypographyComponent({ component: 'h5', defaultFontSize: '0.875rem' }) // 14px
export const H6 = createTypographyComponent({ component: 'h6', defaultFontSize: '0.8125rem' }) // 13px

// ============================================================================
// COMPONENTES DE TEXTO (Paragraph, Span, Small, Tiny)
// ============================================================================

export const Paragraph = createTypographyComponent({
  component: 'p',
  defaultFontSize: '0.875rem',
  defaultFontWeight: 400,
}) // 14px

export const Span = createTypographyComponent({
  component: 'span',
  defaultFontSize: 'inherit',
  defaultFontWeight: 'inherit',
})

export const Small = createTypographyComponent({ component: 'small', defaultFontSize: '0.75rem' }) // 12px
export const Tiny = createTypographyComponent({
  component: 'span',
  defaultFontSize: '0.625rem',
  defaultFontWeight: 400,
}) // 10px

// ============================================================================
// COMPONENTE ESTILIZADO INDEPENDIENTE
// ============================================================================

export const Heading = styled('h6')(({ theme }) => ({
  margin: 0,
  marginTop: theme.spacing(0.5),
  fontSize: '0.875rem',
  fontWeight: 500,
  color: theme.palette.primary.dark,
}))
